import { Injectable } from '@angular/core';

import { DatabaseService } from './base/database.service';
import { Faq } from '../models/faq';
import { Kyc_Check } from '../models/kyc_check.model';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import {
  KycMismatchAction,
  NamesMismatchNoteData,
  KycDeclinedNote,
  NamesMismatchClassification,
  LogFixAttemptDTO,
} from '../models/kyc-mismatch.model';
import {
  ApplyCriterionPayload,
  FlagForReviewPayload,
  KycCaseDecisionResult,
  KYC_CASE_TASK_TYPE,
  KYC_STATUS_RESOLVED,
  KYC_TERMINAL_STAGES,
  KycCaseTaskMatch,
  KycCriteria,
  KycDocumentSet,
  KycCriteriaParams,
  KycDecision,
  RejectCasePayload,
} from '../models/kyc-case.model';

export type KycProvider = 'mock' | 'thirdfort' | 'textract' | 'veriff' | 'manual';
export type DocumentType = 'passport' | 'driving_license' | 'identity_card';

/** Result of the SomosKycV1 no-QR Textract OCR pre-check (/somos_kyc_ocr_extract). */
export interface KycOcrExtractResult {
  cpf: string | null;
  dob: string | null; // ISO YYYY-MM-DD
  fullName: string | null;
  nameAlternates?: string[];
  documentType?: string | null;
  confidence?: Record<string, number>;
  /** True when Textract failed — the caller should fail open and let the user proceed. */
  extractionFailed?: boolean;
}

/** CPK-5103: request to /kyc_is_document — one capture, before its "ficou boa?" preview. */
export interface KycIsDocumentRequest {
  /** base64 JPEG (no data: prefix), already downscaled. */
  image: string;
  side: 'front' | 'back';
  method: 'camera' | 'upload';
  action_id: string;
  claim_id?: string;
  /** `pdf` when the image is the rendered first page of an uploaded PDF. */
  source?: 'image' | 'pdf';
}

/** CPK-5103: what /kyc_is_document decided (see lambdas/src/endpoint/core/kyc-is-document). */
export interface KycIsDocumentResult {
  check_id?: string;
  /** `limit_blocked` = this refusal started an anti-spam block; `blocked` = one is running. */
  outcome: 'document' | 'not_document' | 'limit_restart' | 'limit_proceed' | 'limit_blocked' | 'blocked';
  is_document: boolean;
  /** True when the check itself failed and the image was let through. */
  fail_open: boolean;
  consecutive_not_document?: number;
  max_consecutive?: number;
  not_document_in_window?: number;
  max_per_window?: number;
  /** When blocked: the moment new images are looked at again (ISO). */
  retry_after?: string | null;
  /** Set when the check did not run (`disabled`, `config_error`). */
  skipped?: string;
}

export interface KycCheckRequest {
  provider: KycProvider;
  documentType: DocumentType;
  user_id: string;
  action_id?: string; // Action ID from outstanding actions
  front_id?: string;
  back_id?: string; // Optional for some document types
  providerAdditionalInfo?: {
    stage?: string;
    passed?: boolean;
  };
}
@Injectable({
  providedIn: 'root',
})
export class KycService extends DatabaseService<Kyc_Check> {
  constructor(private http: HttpClient) {
    super('cp_kyc_check');
    // CPK-5259: edits (manual-check modal, notes) go through the lambda, which enforces the
    // role and CPK-5117 case-scope checks that a direct write with the user's session skipped.
    // Deploy order: the Lambda step goes first, and CPK-5260's read-only RLS on cp_kyc_check only
    // after this bundle is live — an older bundle still writes the table with the user's session.
    this.lambdaNames = { update: 'kyc_check_update' };
  }

  async getAllForUser(id: string): Promise<Kyc_Check[]> {
    if (!id) {
      return [];
    }

    return (
      await this.supabase
        .from('cp_kyc_check')
        .select()
        .eq('checked_user_id', id)
        .order('date_requested', { ascending: true })
    ).data as unknown as Kyc_Check[];
  }

  async getById(id: string): Promise<Kyc_Check> {
    return (
      await this.supabase.from('cp_kyc_check').select().eq('id', id).single()
    ).data as unknown as Kyc_Check;
  }

  /**
   * Busca o último kyc_check criado para um action_id específico de um usuário
   * Útil quando o estado do componente é perdido (ex: refresh da página)
   *
   * @param actionId - ID do action
   * @param userId - ID do usuário (checked_user_id)
   */
  /**
   * Returns the most recent passed check for a user, optionally scoped to a specific action.
   * Includes checks with null action_id to handle legacy records created before action tracking.
   */
  async getPassedCheckForUser(userId: string, actionId?: string): Promise<Kyc_Check | null> {
    if (!userId) return null;

    let query = this.supabase
      .from('cp_kyc_check')
      .select()
      .eq('checked_user_id', userId)
      .eq('passed', true);

    if (actionId) {
      query = query.or(`action_id.eq.${actionId},action_id.is.null`);
    }

    const { data, error } = await query
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[KycService] Error fetching passed check for user:', error);
      return null;
    }

    return data as unknown as Kyc_Check | null;
  }

  async getLatestByActionId(actionId: string, userId: string): Promise<Kyc_Check | null> {
    if (!actionId || !userId) {
      return null;
    }

    const { data, error } = await this.supabase
      .from('cp_kyc_check')
      .select()
      .eq('action_id', actionId)
      .eq('checked_user_id', userId)
      .order('date_requested', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error('[KycService] Error fetching latest kyc_check by action_id and user:', error);
      return null;
    }

    return data as unknown as Kyc_Check;
  }

  async revalidateKycCheck(
    kycCheckId: string,
    confirmAutoAction = false
  ): Promise<{
    message: string;
    data: Kyc_Check;
    validationSummary: {
      allPassed: boolean;
      passedCount: number;
      failedCount: number;
      notesAdded: number;
      notesRemoved: number;
    };
  }> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    // 🇧🇷 SERPRO: quando o cliente confirma a auto-correção (CONFIRM_AUTO_ACTION),
    // enviamos a flag para a lambda aplicar o nome legal oficial e revalidar.
    const body: { kyc_check_id: string; confirmAutoAction?: boolean } = {
      kyc_check_id: kycCheckId,
    };
    if (confirmAutoAction) {
      body.confirmAutoAction = true;
    }

    return firstValueFrom(
      this.http.post<{
        message: string;
        data: Kyc_Check;
        validationSummary: {
          allPassed: boolean;
          passedCount: number;
          failedCount: number;
          notesAdded: number;
          notesRemoved: number;
        };
      }>(
        environment.lambdaUrl + '/revalidate-kyc-check',
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );
  }

  async submitKycCheck(checkRequest: KycCheckRequest): Promise<any> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    return firstValueFrom(
      this.http.post(environment.lambdaUrl + '/kyc_check', checkRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
  }

  /**
   * 🇧🇷 SERPRO name gate: confirm (or reject) the authoritative SERPRO name on a
   * check paused in AWAITING_NAME_CONFIRMATION.
   *  - accept=true  → adopt the SERPRO name and resume into the Veriff document step;
   *  - accept=false → fail the KYC (CANCELLED); the user restarts from scratch.
   */
  async confirmSerproName(
    kycCheckId: string,
    accept: boolean
  ): Promise<{ message: string; details: { kyc_check_id: string; accept: boolean; stage?: string } }> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    return firstValueFrom(
      this.http.post<{
        message: string;
        details: { kyc_check_id: string; accept: boolean; stage?: string };
      }>(
        environment.lambdaUrl + '/kyc_confirm_serpro_name',
        { kyc_check_id: kycCheckId, accept },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
  }

  /**
   * SomosKycV1 no-QR pre-check: run Textract OCR on the already-uploaded document side(s) and return
   * the extracted identity fields. The document must already be in storage (cp_personal_document) —
   * pass the front side id as `frontId` and, when the document has a back, `backId` (info is often
   * split across both sides). The standalone /somos_kyc_ocr_extract lambda downloads them and only
   * extracts (the caller compares against the PII on file). On a Textract/download failure the lambda
   * returns `extractionFailed: true` so the caller fails open.
   */
  async extractOcrIdentity(frontId: string, backId?: string): Promise<KycOcrExtractResult> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const body: { front_id: string; back_id?: string } = { front_id: frontId };
    if (backId) body.back_id = backId;

    const response: any = await firstValueFrom(
      this.http.post(
        environment.lambdaUrl + '/somos_kyc_ocr_extract',
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
    return (response?.details ?? response) as KycOcrExtractResult;
  }

  /**
   * CPK-5103: is there an identity document in this capture at all? Called by the SomosKycV1
   * guided capture right after each front/back photo or file, before its preview. The caller
   * fails open on any error (the network interceptor never shows a modal for this URL).
   */
  async checkIsDocument(body: KycIsDocumentRequest): Promise<KycIsDocumentResult> {
    const token = (await this.supabase.auth.getSession()).data.session
      ?.access_token;

    const response: any = await firstValueFrom(
      this.http.post(environment.lambdaUrl + '/kyc_is_document', body, {
        headers: { Authorization: `Bearer ${token}` },
      })
    );
    return (response?.details ?? response) as KycIsDocumentResult;
  }

  /**
   * Get user's retry count for a specific provider
   * Counts KYC checks with stage=DONE and passed=false
   */
  async getUserRetryCount(
    userId: string,
    providerName: string
  ): Promise<{ retryCount: number; maxRetries: number }> {
    try {
      // Busca todos os checks DONE + passed=false do usuário para esse provider
      const { data, error } = await this.supabase
        .from('cp_kyc_check')
        .select('*')
        .eq('checked_user_id', userId)
        .eq('provider', providerName)
        .eq('stage', 'DONE')
        .eq('passed', false);

      if (error) {
        console.error('[KYC] Error fetching retry count:', error);
        return { retryCount: 0, maxRetries: 3 };
      }

      // Busca maxRetries do usuário (se configurado)
      const { data: userData } = await this.supabase
        .from('cp_user')
        .select('attributes, primary_role')
        .eq('id', userId)
        .single();

      // Admin/Agent não tem limite
      if (userData?.primary_role === 'ADMINISTRATOR' || userData?.primary_role === 'AGENT') {
        return { retryCount: data?.length || 0, maxRetries: Infinity };
      }

      // Usuário comum: pegar do attributes ou usar default 3
      const attributes = userData?.attributes as any;
      const maxRetries = attributes?.kyc?.maxRetries ?? 3;

      return {
        retryCount: data?.length || 0,
        maxRetries,
      };
    } catch (error) {
      console.error('[KYC] Unexpected error fetching retry count:', error);
      return { retryCount: 0, maxRetries: 3 };
    }
  }

  // ============================================================================
  // FIX ATTEMPTS TRACKING
  // ============================================================================

  /**
   * Registra uma tentativa de correção de nome no backend.
   *
   * Fire-and-forget: não bloqueia a UX, engole erros silenciosamente.
   * O backend appenda o attempt em note.data.namesMismatch.fixAttempts[].
   */
  logFixAttempt(dto: LogFixAttemptDTO): void {
    this.supabase.auth
      .getSession()
      .then(({ data }) => {
        const token = data.session?.access_token;
        if (!token) {
          console.warn('[KYC] logFixAttempt: no auth token, skipping');
          return;
        }

        firstValueFrom(
          this.http.post(
            environment.lambdaUrl + '/kyc-name-fix-attempt',
            dto,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          )
        ).catch((err) => {
          console.warn('[KYC] logFixAttempt failed (non-blocking):', err?.message || err);
        });
      })
      .catch((err) => {
        console.warn('[KYC] logFixAttempt session error (non-blocking):', err?.message || err);
      });
  }

  // ============================================================================
  // NAMES MISMATCH HELPERS
  // ============================================================================

  /**
   * Extrai dados de names mismatch do provider_return
   * @param kycCheck - KYC check a analisar
   * @returns Classificação de mismatch se existir
   */
  getNamesMismatchFromProviderReturn(
    kycCheck: Kyc_Check
  ): NamesMismatchClassification | null {
    if (!kycCheck?.provider_return) {
      return null;
    }

    const providerReturn = kycCheck.provider_return;

    // Verifica se tem names_mismatch_classification no provider_return
    if (
      providerReturn.names_mismatch_classification &&
      providerReturn.names_mismatch_classification.names_mismatch === true
    ) {
      return providerReturn.names_mismatch_classification as NamesMismatchClassification;
    }

    return null;
  }

  /**
   * Extrai dados de names mismatch das notas estruturadas
   * @param kycCheck - KYC check a analisar
   * @returns Dados de mismatch da última nota se existir
   */
  getNamesMismatchFromNotes(kycCheck: Kyc_Check): NamesMismatchNoteData | null {
    if (!kycCheck?.notes || !Array.isArray(kycCheck.notes)) {
      return null;
    }

    // Procurar a nota mais recente com namesMismatch
    const mismatchNote = [...kycCheck.notes]
      .reverse()
      .find(
        (note: any) =>
          note?.data?.namesMismatch && note.data.namesMismatch.action
      );

    if (mismatchNote?.data?.namesMismatch) {
      return mismatchNote.data.namesMismatch as NamesMismatchNoteData;
    }

    return null;
  }

  /**
   * Verifica se há names mismatch no KYC check
   * @param kycCheck - KYC check a verificar
   * @returns true se houver mismatch detectado
   */
  hasNamesMismatch(kycCheck: Kyc_Check): boolean {
    return (
      this.getNamesMismatchFromProviderReturn(kycCheck) !== null ||
      this.getNamesMismatchFromNotes(kycCheck) !== null
    );
  }

  /**
   * Retorna a action de mismatch (prioritiza notes sobre provider_return)
   * @param kycCheck - KYC check a analisar
   * @returns Action de mismatch ou null
   */
  getNamesMismatchAction(kycCheck: Kyc_Check): KycMismatchAction | null {
    // Priorizar notes (mais atual após revalidações)
    const noteData = this.getNamesMismatchFromNotes(kycCheck);
    if (noteData?.action) {
      return noteData.action;
    }

    // Fallback para provider_return
    const providerData = this.getNamesMismatchFromProviderReturn(kycCheck);
    if (providerData?.mismatchAction) {
      return providerData.mismatchAction;
    }

    return null;
  }

  /**
   * Verifica se o mismatch bloqueia o KYC (apenas REQUEST_NEW_DOCUMENT bloqueia)
   * @param kycCheck - KYC check a verificar
   * @returns true se bloqueado
   */
  isNamesMismatchBlocked(kycCheck: Kyc_Check): boolean {
    const action = this.getNamesMismatchAction(kycCheck);
    return action === 'REUPLOAD';
  }

  /**
   * Verifica se deve mostrar modal de correção de nome
   * @param kycCheck - KYC check a verificar
   * @returns true se deve mostrar modal
   */
  shouldShowNameFixModal(kycCheck: Kyc_Check): boolean {
    const action = this.getNamesMismatchAction(kycCheck);
    if (!action) return false;
    return ['ACCEPT_CHANGES', 'VERIFY_CHANGES', 'FIX_CHANGES'].includes(action);
  }

  /**
   * Obtém mensagem amigável para exibir ao usuário baseada na action
   * @param action - Action de mismatch
   * @param lang - Idioma (pt ou en)
   * @returns Mensagem formatada
   */
  getNamesMismatchMessage(
    action: KycMismatchAction,
    lang: 'pt' | 'en' = 'pt'
  ): string {
    const messages: Record<KycMismatchAction, { pt: string; en: string }> = {
      MATCH: {
        pt: 'Nome validado automaticamente.',
        en: 'Name validated automatically.',
      },
      AUTO_ACCEPT: {
        pt: 'Nome validado automaticamente.',
        en: 'Name validated automatically.',
      },
      ACCEPT_CHANGES: {
        pt: 'Encontramos pequenas diferenças no nome. Deseja aceitar a correção?',
        en: 'We found small differences in the name. Do you want to accept the correction?',
      },
      VERIFY_CHANGES: {
        pt: 'Há diferenças no nome. Por favor, verifique qual está correto.',
        en: 'There are differences in the name. Please verify which one is correct.',
      },
      CONFIRM_AUTO_ACTION: {
        pt: 'Encontramos seu nome legal no registro oficial (CPF). Deseja confirmar a correção automática?',
        en: 'We found your legal name in the official record (CPF). Do you want to confirm the automatic correction?',
      },
      REVIEW_AND_RETRY: {
        pt: 'Confira seus dados (nome, CPF, data de nascimento) e tente novamente.',
        en: 'Please review your details (name, CPF, date of birth) and try again.',
      },
      FIX_CHANGES: {
        pt: 'O nome inserido não confere com o documento. Por favor, corrija.',
        en: 'The name entered does not match the document. Please correct it.',
      },
      REUPLOAD: {
        pt: 'Os nomes são muito diferentes. Por favor, envie um documento com o nome correto.',
        en: 'Names are very different. Please submit a document with the correct name.',
      },
      MANUAL_REVIEW: {
        pt: 'Não foi possível validar automaticamente. Seu caso será analisado manualmente.',
        en: 'Could not validate automatically. Your case will be reviewed manually.',
      },
      DATA_MISSING: {
        pt: 'Dados faltando para validação.',
        en: 'Data missing for validation.',
      },
    };

    return messages[action]?.[lang] || messages.MANUAL_REVIEW[lang];
  }

  // ============================================================================
  // AGENT KYC ACTIONS
  // ============================================================================

  /**
   * Agent action: increase KYC max retries by +1 for a user.
   * Requires an impersonation token (impersonated_by claim).
   */
  async agentIncreaseRetries(userId: string): Promise<{ previousMax: number; newMax: number }> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;
    const res = await firstValueFrom(
      this.http.post<{ message: string; details: { previousMax: number; newMax: number } }>(
        environment.lambdaUrl + '/kyc-agent-action',
        { action: 'increaseRetries', user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
    return res.details;
  }

  /**
   * Agent action: bypass KYC entirely for a user.
   * Requires an impersonation token (impersonated_by claim).
   * When `actionId` is provided the lambda also persists a SKIPPED_BY_AGENT
   * row in cp_kyc_check carrying the IC_AGENT_FLOW flag, which is what
   * unblocks downstream signature prerequisites.
   */
  async agentBypassKyc(
    userId: string,
    bypassReason?: KycSkipReason | string,
    actionId?: string,
  ): Promise<AgentBypassKycResult> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;
    const res = await firstValueFrom(
      this.http.post<{ message: string; details: AgentBypassKycResult }>(
        environment.lambdaUrl + '/kyc-agent-action',
        {
          action: 'bypassKyc',
          user_id: userId,
          bypass_reason: bypassReason ?? null,
          action_id: actionId ?? null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    );
    return res.details;
  }

  // ============================================================================
  // KYC CASE — governed decision loop (cp_kyc_case)
  // ============================================================================

  private async authHeader(): Promise<{ Authorization: string }> {
    const token = (await this.supabase.auth.getSession()).data.session?.access_token;
    return { Authorization: `Bearer ${token}` };
  }

  /**
   * Apply a named criterion to pass a KYC case (MANUAL_APPROVE).
   * Records an append-only cp_kyc_decision referencing the criterion and inserts a clearing check.
   */
  async applyCriterion(payload: ApplyCriterionPayload): Promise<KycCaseDecisionResult> {
    // Backend derives kyc_check_id/checked_user_id from the task; send only what it accepts.
    const body: {
      task_id: string;
      criteria_id: string;
      justification?: string;
      criteria_params?: KycCriteriaParams;
      resulting_kyc_check_id?: string;
    } = {
      task_id: payload.task_id!,
      criteria_id: payload.criteria_id,
    };
    if (payload.justification) {
      body.justification = payload.justification;
    }
    if (payload.criteria_params) {
      body.criteria_params = payload.criteria_params;
    }
    if (payload.resulting_kyc_check_id) {
      body.resulting_kyc_check_id = payload.resulting_kyc_check_id;
    }
    return firstValueFrom(
      this.http.post<KycCaseDecisionResult>(
        environment.lambdaUrl + '/kyc-case/apply-criterion',
        body,
        { headers: await this.authHeader() }
      )
    );
  }

  /**
   * Flag a case for review (FLAGGED_FOR_REVIEW) — no criterion fits and the agent is unsure.
   * Records a decision with the proposed rationale; the KYC is NOT cleared.
   */
  async flagForReview(payload: FlagForReviewPayload): Promise<KycCaseDecisionResult> {
    const body: {
      task_id: string;
      justification: string;
      criteria_params?: KycCriteriaParams;
    } = { task_id: payload.task_id!, justification: payload.justification };
    if (payload.criteria_params) {
      body.criteria_params = payload.criteria_params;
    }
    return firstValueFrom(
      this.http.post<KycCaseDecisionResult>(
        environment.lambdaUrl + '/kyc-case/flag',
        body,
        { headers: await this.authHeader() }
      )
    );
  }

  /**
   * Reject the KYC (MANUAL_REJECT) — records the rejection with agent identity; no clearing check.
   */
  async rejectCase(payload: RejectCasePayload): Promise<KycCaseDecisionResult> {
    const body: {
      task_id: string;
      reason_code?: string;
      justification: string;
      criteria_id?: string;
      criteria_params?: KycCriteriaParams;
    } = {
      task_id: payload.task_id!,
      justification: payload.justification,
    };
    if (payload.reason_code) {
      body.reason_code = payload.reason_code;
    }
    if (payload.criteria_id) {
      body.criteria_id = payload.criteria_id;
    }
    if (payload.criteria_params) {
      body.criteria_params = payload.criteria_params;
    }
    return firstValueFrom(
      this.http.post<KycCaseDecisionResult>(
        environment.lambdaUrl + '/kyc-case/reject',
        body,
        { headers: await this.authHeader() }
      )
    );
  }

  /**
   * The document sets from this client's previous KYC attempts, newest first, so a review can
   * re-run a verification without asking the client to capture again.
   *
   * The authoritative front/back mapping lives on each check's
   * `requested_attributes.cpPersonalDocumentsId` — NOT on cp_personal_document, whose
   * `doc_type` is the upload filename and cannot tell the two sides apart.
   */
  async listReusableDocumentSets(userId: string): Promise<KycDocumentSet[]> {
    if (!userId) return [];

    const { data, error } = await this.supabase
      .from('cp_kyc_check')
      .select('id, provider, passed, stage, created_at, requested_attributes')
      .eq('checked_user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[KycService] Error listing reusable documents:', error);
      return [];
    }

    const sets: KycDocumentSet[] = [];
    for (const check of (data ?? []) as any[]) {
      const attrs = check.requested_attributes ?? {};
      const ids = attrs.cpPersonalDocumentsId ?? {};
      if (!ids.front_id) continue;

      sets.push({
        sourceCheckId: check.id,
        provider: check.provider ?? null,
        sourceCheckPassed: check.passed ?? null,
        sourceCheckCreatedAt: check.created_at ?? null,
        documentType: attrs.documentType ?? null,
        frontId: ids.front_id,
        backId: ids.back_id ?? null,
      });
    }
    return sets;
  }

  /**
   * Poll a check until the provider reaches a terminal stage.
   *
   * A new KYC is decided by webhook, not by the POST that created it, so the caller cannot
   * read a verdict off the response. Resolves with the check once it settles, or null on
   * timeout — a timeout is "still processing", never "failed".
   */
  async waitForCheckResult(
    checkId: string,
    options: { timeoutMs?: number; intervalMs?: number } = {}
  ): Promise<Kyc_Check | null> {
    const timeoutMs = options.timeoutMs ?? 120_000;
    const intervalMs = options.intervalMs ?? 3_000;
    const deadline = Date.now() + timeoutMs;

    while (Date.now() < deadline) {
      const check = await this.getById(checkId);
      if (check && KYC_TERMINAL_STAGES.includes(check.stage)) {
        return check;
      }
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    return null;
  }

  /**
   * List active pass-criteria from cp_kyc_criteria, optionally scoped to a failure class.
   * @param appliesTo the failure class to filter on (e.g. `NAME_MISMATCH`); omit for all active criteria.
   */
  async listCriteria(appliesTo?: string): Promise<KycCriteria[]> {
    let query = this.supabase
      .from('cp_kyc_criteria')
      .select('*')
      .eq('active', true);

    if (appliesTo) {
      query = query.eq('applies_to', appliesTo);
    }

    const { data, error } = await query.order('title', { ascending: true });
    if (error) {
      console.error('[KycService] Error fetching criteria:', error);
      return [];
    }
    return (data ?? []) as unknown as KycCriteria[];
  }

  /**
   * The cp_kyc_check a kyc_case task was opened on.
   *
   * Arriving from the kanban gives the page a task id but not the attempt it is about, and a
   * client can have several. Without this the KYC page cannot point at the right one, and the
   * review modal cannot tell that the check the agent clicked is not the one under review.
   */
  async getCaseTaskCheckId(taskId: string): Promise<string | null> {
    if (!taskId) return null;

    const { data, error } = await this.supabase
      .from('cp_task')
      .select('data')
      .eq('id', taskId)
      .maybeSingle();

    if (error) {
      console.error('[KycService] Error reading case task linkage:', error);
      return null;
    }
    return (data?.data as { kyc_check_id?: string } | null)?.kyc_check_id ?? null;
  }

  /**
   * Find the OPEN kyc_case task a review should be recorded against.
   *
   * The kanban hands the id over as `?task_src=`, but the KYC page is also reachable
   * directly, and every decision endpoint keys on task_id — so the modal has to be able to
   * find the case itself. Two-step match, because a client accumulates attempts and the case
   * is opened on whichever one failed:
   *
   *   1. the task opened for THIS check — the unambiguous match;
   *   2. failing that, any other open case for the same client. The agent is looking at a
   *      later attempt of a case that is still open; that is the case their decision belongs
   *      to, and the modal says so rather than pretending the match was exact.
   *
   * Resolved tasks are excluded in both steps: a settled case must not take a second
   * decision. Returns null when the client has no open case — the caller then runs read-only.
   */
  async findOpenCaseTask(
    clientId: string,
    kycCheckId: string
  ): Promise<KycCaseTaskMatch | null> {
    if (!clientId) return null;

    const openStatusIds = await this.openKycCaseStatusIds();
    if (openStatusIds.length === 0) return null;

    const { data, error } = await this.supabase
      .from('cp_task')
      .select('id, data')
      .eq('type', KYC_CASE_TASK_TYPE)
      .eq('client_id', clientId)
      .in('status_id', openStatusIds)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[KycService] Error finding open case task:', error);
      return null;
    }

    const tasks = (data ?? []) as Array<{ id: string; data: unknown }>;
    if (tasks.length === 0) return null;

    const checkIdOf = (task: { data: unknown }): string | null =>
      (task.data as { kyc_check_id?: string } | null)?.kyc_check_id ?? null;

    const exact = tasks.find((task) => checkIdOf(task) === kycCheckId);
    if (exact) {
      return { taskId: exact.id, exactCheck: true, caseKycCheckId: kycCheckId };
    }

    const fallback = tasks[0];
    return {
      taskId: fallback.id,
      exactCheck: false,
      caseKycCheckId: checkIdOf(fallback),
    };
  }

  /**
   * Open a kyc_case for a failed check that has none, so the review can be recorded.
   *
   * Cases are normally raised by notify-kyc's hook; a check it never fired for leaves an agent
   * able to inspect and re-verify while unable to record a decision. Idempotent server-side —
   * an existing open case is returned rather than duplicated.
   */
  async openCaseForCheck(kycCheckId: string): Promise<string | null> {
    const response = await firstValueFrom(
      this.http.post<{ details?: { task_id?: string }; task_id?: string }>(
        environment.lambdaUrl + '/kyc-case/open',
        { kyc_check_id: kycCheckId },
        { headers: await this.authHeader() }
      )
    );
    // retSuccess nests the payload under `details`; tolerate both shapes.
    return response?.details?.task_id ?? response?.task_id ?? null;
  }

  /**
   * kyc_case status ids that are not terminal. Derived by excluding 'Resolved' rather than
   * listing the open ones, so a status added later counts as open until told otherwise.
   */
  private async openKycCaseStatusIds(): Promise<string[]> {
    const { data, error } = await this.supabase
      .from('cp_task_status')
      .select('id, label')
      .eq('task_type', KYC_CASE_TASK_TYPE);

    if (error) {
      console.error('[KycService] Error loading kyc_case statuses:', error);
      return [];
    }
    return (data ?? [])
      .filter((status: { label: string | null }) => status.label !== KYC_STATUS_RESOLVED)
      .map((status: { id: string }) => status.id);
  }

  /**
   * Full auditable decision chain for a subject: all cp_kyc_decision rows ordered oldest-first.
   */
  async getDecisionTimeline(checkedUserId: string): Promise<KycDecision[]> {
    if (!checkedUserId) return [];

    const { data, error } = await this.supabase
      .from('cp_kyc_decision')
      .select('*, agent:cp_user!cp_kyc_decision_agent_id_fkey(display_name)')
      .eq('checked_user_id', checkedUserId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[KycService] Error fetching decision timeline:', error);
      return [];
    }
    return (data ?? []) as unknown as KycDecision[];
  }

  /**
   * Index a client's decisions by the check each one PRODUCED (`resulting_kyc_check_id`).
   *
   * A resolution check carries only `{source, decision_type, resolver_type, cleared_check_id}`
   * in its notes — the criterion and the justification live on cp_kyc_decision. Without this
   * lookup the KYC list shows a passing check with no visible reason for it, which is the one
   * thing a reviewer reading history actually needs.
   */
  async getDecisionsByResultingCheck(
    checkedUserId: string
  ): Promise<Record<string, KycDecision>> {
    const decisions = await this.getDecisionTimeline(checkedUserId);
    const byCheck: Record<string, KycDecision> = {};
    for (const decision of decisions) {
      if (decision.resulting_kyc_check_id) {
        byCheck[decision.resulting_kyc_check_id] = decision;
      }
    }
    return byCheck;
  }
}

export const KYC_SKIP_REASONS = [
  'DOC_EMAIL_UPLOAD_FAIL',
  'GOOD_DOC_CANT_PROCEED',
  'UNDERAGED',
  'OLD_DOC',
  'EXPIRED_CNH',
  'STAINED_DOC',
  'PLASTICIZED_DOC',
  'OTHER',
  'DONT_KNOW',
] as const;
export type KycSkipReason = typeof KYC_SKIP_REASONS[number];

export interface AgentBypassKycResult {
  bypassedAt: string;
  claimsFlagged: number;
  kycCheckId?: string;
  reason?: KycSkipReason | null;
}
